import { useEffect, useMemo, useRef } from 'react';

// Seamless looping video backdrop — double-buffered crossfade (the pro
// no-re-encode loop technique: blend the clip's end into its start).
// Iris-Xe-friendly WITHOUT any aesthetic cost:
//  - preload="none": NOTHING downloads or primes a decoder until the section
//    approaches the viewport (multiple backdrops never load simultaneously)
//  - IntersectionObserver starts/stops decoding as the section enters/leaves
//  - the standby buffer stays completely cold until its crossfade moment —
//    it then pulls from HTTP cache, and the 1.15s fade masks decoder spin-up
//  - videos live on their own compositor layer (translateZ hint in CSS)
// Reduced motion holds a still frame.
const CROSSFADE = 1.15; // seconds

export function VideoBackdrop({ src, className = '', scrim = true }) {
  const rootRef = useRef(null);
  const refA = useRef(null);
  const refB = useRef(null);
  const state = useRef({ active: 0, fading: false, raf: 0, inView: false });

  const reduced = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  useEffect(() => {
    const videos = [refA.current, refB.current];
    const root = rootRef.current;
    if (!videos[0] || !videos[1] || !root) return undefined;

    if (reduced) {
      videos[0].preload = 'metadata';
      videos[0].addEventListener(
        'loadedmetadata',
        () => {
          videos[0].currentTime = 0.02;
        },
        { once: true },
      );
      videos[0].load();
      videos[0].classList.add('is-active');
      return undefined;
    }

    videos[0].classList.add('is-active');

    function tick() {
      const st = state.current;
      const current = videos[st.active];
      const standby = videos[1 - st.active];
      if (st.inView && !st.fading && current.duration && current.currentTime > current.duration - CROSSFADE) {
        st.fading = true;
        standby.currentTime = 0;
        standby.play().catch(() => {});
        standby.classList.add('is-active');
        current.classList.remove('is-active');
        window.setTimeout(() => {
          current.pause();
          st.active = 1 - st.active;
          st.fading = false;
        }, CROSSFADE * 1000 + 80);
      }
      st.raf = requestAnimationFrame(tick);
    }

    // decode only while visible — the single biggest weak-GPU win
    const io = new IntersectionObserver(
      ([entry]) => {
        const st = state.current;
        st.inView = entry.isIntersecting;
        const current = videos[st.active];
        if (st.inView) {
          current.play().catch(() => {});
          if (!st.raf) st.raf = requestAnimationFrame(tick);
        } else {
          videos.forEach((v) => v.pause());
          cancelAnimationFrame(st.raf);
          st.raf = 0;
        }
      },
      { rootMargin: '120px 0px' },
    );
    io.observe(root);

    return () => {
      io.disconnect();
      cancelAnimationFrame(state.current.raf);
      videos.forEach((v) => v.pause());
    };
  }, [reduced]);

  return (
    <div ref={rootRef} className={`video-backdrop ${className}`} aria-hidden="true">
      <video ref={refA} src={src} muted playsInline preload="none" />
      <video ref={refB} src={src} muted playsInline preload="none" />
      {scrim && <div className="video-backdrop-scrim" />}
    </div>
  );
}
